import { useEffect, useRef, useState } from "react";
import { api } from "../api.js";

let libraryPromise;
let initializedClientId;
let activeCredentialHandler;

function loadGoogleLibrary() {
  if (window.google?.accounts?.id) return Promise.resolve(window.google);
  if (!libraryPromise) {
    libraryPromise = new Promise((resolve, reject) => {
      const existing = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
      const script = existing || document.createElement("script");
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true;
      script.defer = true;
      script.onload = () => resolve(window.google);
      script.onerror = () => reject(new Error("Google Sign-In could not be loaded"));
      if (!existing) document.head.append(script);
    });
  }
  return libraryPromise;
}

function initializeGoogle(clientId) {
  if (initializedClientId === clientId) return;
  window.google.accounts.id.initialize({
    client_id: clientId,
    callback: (response) => activeCredentialHandler?.(response.credential),
    use_fedcm_for_button: true,
    auto_select: false,
  });
  initializedClientId = clientId;
}

export default function GoogleSignIn({ onCredential, onError, divider = "or continue with email" }) {
  const buttonRef = useRef(null);
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    let cancelled = false;
    activeCredentialHandler = onCredential;
    api.authProviders()
      .then(async ({ google }) => {
        if (!google.enabled || cancelled) return;
        await loadGoogleLibrary();
        if (cancelled || !buttonRef.current) return;
        initializeGoogle(google.client_id);
        activeCredentialHandler = onCredential;
        buttonRef.current.replaceChildren();
        window.google.accounts.id.renderButton(buttonRef.current, {
          type: "standard",
          theme: "outline",
          size: "large",
          text: "continue_with",
          shape: "rectangular",
          logo_alignment: "left",
          width: Math.min(Math.max(buttonRef.current.clientWidth, 240), 400),
        });
        setEnabled(true);
      })
      .catch((error) => { if (!cancelled) onError(error); });
    return () => {
      cancelled = true;
      if (activeCredentialHandler === onCredential) activeCredentialHandler = null;
    };
  }, [onCredential, onError]);

  return (
    <div className={enabled ? "grid gap-5" : "min-h-0"}>
      <div ref={buttonRef} className="min-h-0 w-full overflow-hidden [&>div]:!w-full" aria-label="Continue with Google" />
      {enabled && <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 text-[10px] font-bold uppercase tracking-[.12em] text-muted-foreground"><i className="h-px bg-neutral-100" /><span>{divider}</span><i className="h-px bg-neutral-100" /></div>}
    </div>
  );
}
